import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material'
import { schoolApiSlice, SchoolStudent } from '../../../features/school/schoolSlice'
import ClassPicker from './ClassPicker'

interface Props {
    schoolId: number
    onClose: () => void
}

const EMPTY = { firstName: '', lastName: '', grade: 0, literal: '' }

const AddStudentDialog: React.FC<Props> = ({ schoolId, onClose }) => {
    const { formatMessage } = useIntl()
    const [form, setForm] = useState(EMPTY)
    const [duplicate, setDuplicate] = useState(false)
    const [created, setCreated] = useState<SchoolStudent | null>(null)
    const [addStudent, { isLoading }] = schoolApiSlice.useAddSchoolStudentMutation()

    const update = (patch: Partial<typeof EMPTY>) => {
        setDuplicate(false)
        setForm({ ...form, ...patch })
    }

    const submit = async (allowDuplicates: boolean) => {
        try {
            const row = await addStudent({
                schoolId,
                firstName: form.firstName,
                lastName: form.lastName,
                grade: String(form.grade),
                literal: form.literal,
                allowDuplicates,
            }).unwrap()
            if (row.status === 'OK' && row.student) {
                setCreated(row.student)
                // класс оставляем: обычно добавляют подряд учеников одного класса
                setForm({ ...EMPTY, grade: form.grade, literal: form.literal })
                setDuplicate(false)
            } else if (row.status === 'DUPLICATE_IN_SCHOOL') {
                setDuplicate(true)
            } else {
                toast.error(formatMessage({ id: `schoolImport_${row.status}` }))
            }
        } catch { /* общий обработчик */ }
    }

    const canSubmit = form.firstName.trim() && form.lastName.trim() && form.grade

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>{formatMessage({ id: 'schoolAddStudent' })}</DialogTitle>
            <DialogContent>
                {created && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {created.firstName} {created.lastName}: {formatMessage({ id: 'schoolLogin' })}{' '}
                        <b>{created.username}</b>, {formatMessage({ id: 'temporaryPassword' }).toLowerCase()}{' '}
                        <b>{created.tempPassword}</b>
                    </Alert>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1, mb: 2.5 }}>
                    <TextField size="small" autoFocus label={formatMessage({ id: 'firstName' })} value={form.firstName}
                        onChange={(e) => update({ firstName: e.target.value })} />
                    <TextField size="small" label={formatMessage({ id: 'lastName' })} value={form.lastName}
                        onChange={(e) => update({ lastName: e.target.value })} />
                </Box>
                <ClassPicker grade={form.grade} literal={form.literal}
                    onChange={(grade, literal) => update({ grade, literal })} />
                {duplicate && (
                    <Alert severity="warning" sx={{ mt: 2 }}>{formatMessage({ id: 'schoolDuplicateWarning' })}</Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>{formatMessage({ id: 'close' })}</Button>
                {duplicate ? (
                    <Button variant="contained" color="warning" disabled={isLoading} onClick={() => submit(true)}>
                        {formatMessage({ id: 'schoolAddAnyway' })}
                    </Button>
                ) : (
                    <Button variant="contained" disabled={!canSubmit || isLoading} onClick={() => submit(false)}>
                        {formatMessage({ id: 'add' })}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default AddStudentDialog
